// Globals
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { StyledPageTitle, StyledPageWrapper } from '../../components/Styled';
import { SeoCategoryTree, SeoFilterGroups } from '../../components';
import { HasUnsavedChangesModal } from '@/components';
import { StyledHeaderBlock, StyledLayout, StyledDetails, StyledPlaceholder } from './SeoFiltersContainerStyles';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Hooks
import { useBoolean } from '@/hooks';
import { useSeoFilterEditor } from '../../hooks';

interface ISelectedCategory {
  id: string;
  name: string;
}

const SeoFiltersContainer = () => {
  const { t } = useTranslation(['seo']);
  const { canManage } = usePermissions();
  const canManageSeo = canManage(Resource.SEO);

  const [selectedCategory, setSelectedCategory] = useState<ISelectedCategory | null>(null);
  const [pendingCategory, setPendingCategory] = useState<ISelectedCategory | null>(null);
  const { value: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBoolean();

  const editor = useSeoFilterEditor(selectedCategory?.id ?? null);

  // Guard category switches: if there are unsaved changes, defer the switch and
  // ask the user what to do with them.
  const handleSelectCategory = (category: ISelectedCategory) => {
    if (category.id === selectedCategory?.id) return;

    if (editor.isDirty) {
      setPendingCategory(category);
      openModal();
      return;
    }

    setSelectedCategory(category);
  };

  const applyPendingCategory = () => {
    setSelectedCategory(pendingCategory);
    setPendingCategory(null);
    closeModal();
  };

  const handleDiscardAndSwitch = () => {
    applyPendingCategory();
  };

  const handleSaveAndSwitch = async () => {
    const saved = await editor.save();
    if (saved) {
      applyPendingCategory();
    }
  };

  const handleCancelSwitch = () => {
    setPendingCategory(null);
    closeModal();
  };

  return (
    <StyledPageWrapper>
      <StyledHeaderBlock>
        <StyledPageTitle>{t('seo_filters_tab_title')}</StyledPageTitle>
      </StyledHeaderBlock>

      <StyledLayout>
        <SeoCategoryTree selectedId={selectedCategory?.id ?? null} onSelect={handleSelectCategory} />

        <StyledDetails>
          {selectedCategory ? (
            <SeoFilterGroups categoryName={selectedCategory.name} canManage={canManageSeo} editor={editor} />
          ) : (
            <StyledPlaceholder>{t('seo_filters_select_category_hint')}</StyledPlaceholder>
          )}
        </StyledDetails>
      </StyledLayout>

      <HasUnsavedChangesModal
        isOpened={isModalOpen}
        handleCloseModal={handleCancelSwitch}
        handleSaveAndClose={handleSaveAndSwitch}
        handleExitAndClose={handleDiscardAndSwitch}
      />
    </StyledPageWrapper>
  );
};

export default SeoFiltersContainer;
